// src/utils/authRedirect.js

/**
 * Destinazione post-login ("torna alla pagina che avevi chiesto").
 *
 * - Viaggia in query: /login?redirect=<percorso codificato> (accettato anche
 *   ?next= per compatibilita'). I link tra login / registrazione / password
 *   dimenticata la conservano (withRedirect).
 * - Copia in localStorage con scadenza 30 minuti, per i passaggi che perdono
 *   la query (es. reset password aperto dall'email). Cancellata al primo
 *   utilizzo, a ogni login riuscito (usata o no) e al logout.
 * - Nel link di verifica email la porta il server (utils/safeRedirect.js,
 *   stesse regole di sanitizeRedirect qui sotto).
 *
 * Sicurezza: solo percorsi interni ("/..." ma non "//" o "/\"), niente
 * schemi, niente pagine di auth (loop). Percorso non valido o non ammesso
 * per il ruolo -> destinazione di sempre.
 */

const STORAGE_KEY = "stir_post_login_redirect";
const TTL_MS = 30 * 60 * 1000;
const MAX_LEN = 512;
const PROBE_ORIGIN = "https://app.invalid";

const AUTH_PATHS = [
  "/login",
  "/register",
  "/supplier-register",
  "/forgot",
  "/reset-password",
  "/email-confermata",
];

// backslash e caratteri di controllo (0-31, 127): alcuni browser li
// normalizzano in "/" o li scartano
function hasUnsafeChars(s) {
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c === 92 || c < 32 || c === 127) return true;
  }
  return false;
}

function isUnder(path, prefix) {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** Percorso interno normalizzato (pathname + search + hash) oppure null. */
export function sanitizeRedirect(raw) {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s || s.length > MAX_LEN) return null;
  if (!s.startsWith("/") || s.startsWith("//") || s.startsWith("/\\")) {
    return null;
  }
  if (hasUnsafeChars(s)) return null;

  let u;
  try {
    u = new URL(s, PROBE_ORIGIN);
  } catch {
    return null;
  }
  if (u.origin !== PROBE_ORIGIN) return null;

  const out = `${u.pathname}${u.search}${u.hash}`;
  // "/..//host" viene normalizzato in "//host": ricontrollo sull'output
  if (out.startsWith("//") || out.startsWith("/\\")) return null;

  // le route di React Router non distinguono maiuscole/minuscole
  const path = (u.pathname.replace(/\/+$/, "") || "/").toLowerCase();
  if (path === "/") return null;
  if (AUTH_PATHS.some((p) => isUnder(path, p))) return null;

  return out;
}

/**
 * Pagine ammesse per ruolo:
 * - user: tutto tranne area admin e area fornitore
 * - fornitore: solo /fornitore/*, /profilo, /pagamenti/*
 * - admin: solo /admin*, /react-admin*
 * Ruolo sconosciuto -> nessun redirect.
 */
export function isAllowedForRole(target, role) {
  let path;
  try {
    path = new URL(target, PROBE_ORIGIN).pathname.toLowerCase();
  } catch {
    return false;
  }
  const r = String(role || "").toLowerCase();
  const adminArea = isUnder(path, "/admin") || isUnder(path, "/react-admin");
  const supplierArea = isUnder(path, "/fornitore");

  if (r === "admin") return adminArea;
  if (r === "fornitore") {
    return supplierArea || path === "/profilo" || isUnder(path, "/pagamenti");
  }
  if (r === "user") return !adminArea && !supplierArea;
  return false;
}

export function rememberRedirect(target) {
  const safe = sanitizeRedirect(target);
  if (!safe) return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ path: safe, exp: Date.now() + TTL_MS }),
    );
  } catch {
    /* private mode / quota: resta solo la query */
  }
}

export function clearRememberedRedirect() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function readRememberedRedirect() {
  try {
    const o = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (!o || typeof o.exp !== "number" || Date.now() > o.exp) return null;
    return sanitizeRedirect(o.path);
  } catch {
    return null;
  }
}

/** Destinazione dalla query (?redirect=, alias ?next=), validata. */
export function getRedirectFromSearch(search) {
  const qs = new URLSearchParams(search || "");
  return sanitizeRedirect(qs.get("redirect") || qs.get("next") || "");
}

/** Query o copia locale, SENZA consumarla (es. da inviare alla registrazione). */
export function peekRedirect(search) {
  return getRedirectFromSearch(search) || readRememberedRedirect();
}

/** Aggiunge ?redirect= a un percorso di auth, se c'e' una destinazione. */
export function withRedirect(to, redirect) {
  return redirect ? `${to}?redirect=${encodeURIComponent(redirect)}` : to;
}

/**
 * URL di login che ricorda la pagina corrente (location di React Router).
 * Salva anche la copia locale.
 */
export function buildLoginUrl(location) {
  const target = sanitizeRedirect(
    `${location?.pathname || ""}${location?.search || ""}${location?.hash || ""}`,
  );
  if (!target) return "/login";
  rememberRedirect(target);
  return withRedirect("/login", target);
}

/**
 * Destinazione dopo un login riuscito: redirect (query, poi copia locale) se
 * valido e ammesso per il ruolo, altrimenti `fallback` (home di sempre).
 * Cancella SEMPRE la copia locale.
 */
export function resolvePostLoginPath({ role, search, fallback }) {
  const candidate = peekRedirect(search);
  clearRememberedRedirect();
  return candidate && isAllowedForRole(candidate, role) ? candidate : fallback;
}
